import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface SearchSelectOption {
  value: string;
  label: string;
  sub?: string;
}

interface Props {
  id?: string;
  value: string;
  onChange: (next: string) => void;
  options: SearchSelectOption[];
  /** Label shown for the empty selection (value = ""). Also the first pickable row. */
  emptyLabel: string;
  placeholder?: string;
  disabled?: boolean;
}

/** Bỏ dấu tiếng Việt để tìm không phân biệt dấu. */
function normalize(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .toLowerCase()
    .trim();
}

/**
 * Type-ahead single-select over an already-loaded option list (client-side
 * filtering, accent-insensitive). Used for the Bác sĩ / Dịch vụ pickers on the
 * appointment-create flow where the full list is small and fetched up-front.
 * Keyboard: ↑/↓ move, Enter select, Esc close.
 */
export function SearchSelect({
  id,
  value,
  onChange,
  options,
  emptyLabel,
  placeholder,
  disabled,
}: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value]
  );

  const filtered = useMemo(() => {
    const nq = normalize(q);
    const rows = nq
      ? options.filter(
          (o) => normalize(o.label).includes(nq) || normalize(o.sub ?? "").includes(nq)
        )
      : options;
    // Hàng đầu luôn là lựa chọn "trống" (bác sĩ bất kỳ / không chọn dịch vụ).
    return [{ value: "", label: emptyLabel } as SearchSelectOption, ...rows];
  }, [options, q, emptyLabel]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  function commit(next: string) {
    onChange(next);
    setQ("");
    setOpen(false);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open) {
      if (e.key === "ArrowDown") setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(filtered.length - 1, a + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[active]) commit(filtered[active].value);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div className="relative" ref={wrapRef}>
      <Input
        id={id}
        type="search"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        autoComplete="off"
        disabled={disabled}
        // Khi mở → hiện ô tìm (q); khi đóng → hiện nhãn đã chọn.
        value={open ? q : selected?.label ?? ""}
        placeholder={selected ? selected.label : placeholder ?? emptyLabel}
        onFocus={() => {
          setOpen(true);
          setActive(0);
        }}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onKeyDown={onKeyDown}
      />
      {open && (
        <div
          role="listbox"
          className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-input bg-card p-1 shadow-md"
        >
          {filtered.length === 0 && (
            <div className="px-2 py-6 text-center text-sm text-muted-foreground">
              Không có kết quả
            </div>
          )}
          {filtered.map((o, i) => (
            <div
              key={o.value || "__empty"}
              role="option"
              aria-selected={o.value === value}
              className={cn(
                "flex cursor-pointer flex-col rounded-sm px-2 py-1.5 text-sm text-foreground",
                i === active && "bg-muted"
              )}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                commit(o.value);
              }}
            >
              <div className="font-medium">{o.label}</div>
              {o.sub && (
                <div className="text-xs text-muted-foreground">{o.sub}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
